import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Public "Contactez-nous" form dropped into the landing page.
 * Anyone (no login required) can send a message to the admin inbox.
 */
export default function PublicContactForm() {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", subject: "", message: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 3) {
            toast.error("Nom, e-mail et message sont obligatoires");
            return;
        }
        setSending(true);
        try {
            await api.post("/public/contact", form);
            toast.success("Message envoyé — nous vous répondons rapidement.");
            setSent(true);
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'envoi");
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <div
                className="sd-card p-8 text-center bg-gradient-to-br from-[#F5A623]/10 to-[#F5A623]/5"
                data-testid="public-contact-sent"
            >
                <div className="text-4xl mb-3">✉️</div>
                <div className="font-display text-xl text-[#0B192C] mb-2">Message reçu !</div>
                <p className="text-sm text-slate-600 max-w-sm mx-auto">
                    Nous accusons réception de votre message. L'équipe Renforteo vous
                    répond sous 24h ouvrées, directement sur votre e-mail.
                </p>
                <button
                    onClick={() => setSent(false)}
                    className="btn-ghost-navy text-xs mt-4"
                    data-testid="public-contact-send-another"
                >
                    Envoyer un autre message
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="sd-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="public-contact-form"
        >
            <div>
                <label className="block text-xs font-semibold mb-1 text-slate-700">
                    Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    maxLength={120}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                    data-testid="public-contact-name"
                />
            </div>
            <div>
                <label className="block text-xs font-semibold mb-1 text-slate-700">
                    E-mail <span className="text-red-500">*</span>
                </label>
                <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    maxLength={150}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                    data-testid="public-contact-email"
                />
            </div>
            <div>
                <label className="block text-xs font-semibold mb-1 text-slate-700">Téléphone</label>
                <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    maxLength={40}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                    data-testid="public-contact-phone"
                />
            </div>
            <div>
                <label className="block text-xs font-semibold mb-1 text-slate-700">Sujet</label>
                <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => set("subject", e.target.value)}
                    maxLength={150}
                    placeholder="Ex : Devis événementiel, question, partenariat..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                    data-testid="public-contact-subject"
                />
            </div>
            <div className="md:col-span-2">
                <label className="block text-xs font-semibold mb-1 text-slate-700">
                    Votre message <span className="text-red-500">*</span>
                </label>
                <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    maxLength={5000}
                    placeholder="Bonjour, ..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                    data-testid="public-contact-message"
                />
                <div className="text-[10px] text-slate-400 text-right mt-1">
                    {form.message.length}/5000
                </div>
            </div>
            <div className="md:col-span-2 flex justify-end">
                <button
                    type="submit"
                    disabled={sending}
                    className="btn-gold disabled:opacity-50"
                    data-testid="public-contact-submit"
                >
                    {sending ? "Envoi…" : "Envoyer mon message"}
                </button>
            </div>
        </form>
    );
}
